"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import axios from "axios";

/**
 * Update Multiple for Production Tracking.
 * Lists dates that have saved timesheet data (the same dates a production row
 * can be edited for), then saves Newly Installed and Daily Notes per date.
 */

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
        fill="#424143"
      />
    </svg>
  );
}

const PARTIAL_NUMBER_INPUT_RE = /^\d*\.?\d*$/;

const isValidPartialNumberInput = (value) =>
  value === "" || PARTIAL_NUMBER_INPUT_RE.test(value);

const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatIsoDateLabel = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string") return "--";
  const parts = isoDate.split("-");
  if (parts.length !== 3) return isoDate;
  const [yyyy, mm, dd] = parts;
  return `${mm}/${dd}/${yyyy.slice(-2)}`;
};

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

const fieldKey = (workDate, itemKey) => `${workDate}::${itemKey}`;

const blankField = () => ({ newlyInstalled: "0", dailyNotes: "" });

function fieldsFromExisting(dates, templateItems, existingByDate) {
  const next = {};
  (dates || []).forEach((workDate) => {
    const saved = existingByDate?.[workDate] || {};
    (templateItems || []).forEach((item) => {
      const prior = saved[item.itemKey];
      next[fieldKey(workDate, item.itemKey)] = prior
        ? {
            newlyInstalled:
              prior.newlyInstalled == null || prior.newlyInstalled === ""
                ? "0"
                : String(prior.newlyInstalled),
            dailyNotes: prior.dailyNotes || "",
          }
        : blankField();
    });
  });
  return next;
}

function ProductionTrackingUpdateMultipleModal({
  isOpen,
  onClose,
  projectNo,
  scopeId,
  scopeName,
  templateItems = [],
  existingByDate = {},
  ensureDates = [],
  getAuthConfig,
  onSave,
}) {
  const [dates, setDates] = React.useState([]);
  const [datesLoading, setDatesLoading] = React.useState(false);
  const [datesError, setDatesError] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState({});
  const [fields, setFields] = React.useState({});
  const [saveMessage, setSaveMessage] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const selectAllRef = React.useRef(null);
  const requestSeqRef = React.useRef(0);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, saving]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const requestSeq = ++requestSeqRef.current;
    const controller = new AbortController();
    setSelectedIds({});
    setSaveMessage(null);
    setDatesError("");
    setDates([]);
    setFields({});
    setDatesLoading(true);

    const loadDates = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_BASEURL;
        const params = new URLSearchParams();
        if (scopeName) params.set("scope", scopeName);
        if (scopeId) params.set("scopeId", scopeId);
        const qs = params.toString();
        const url = `${base}/api/miter/production-performance/${encodeURIComponent(projectNo)}/production-editable-dates${qs ? `?${qs}` : ""}`;
        const config = typeof getAuthConfig === "function"
          ? await getAuthConfig({ signal: controller.signal })
          : { signal: controller.signal };
        const res = await axios.get(url, config);
        if (requestSeq !== requestSeqRef.current) return;
        if (res.data?.status !== "success") {
          throw new Error(res.data?.message || "Failed to load dates.");
        }
        const fromApi = Array.isArray(res.data?.data?.dates) ? res.data.data.dates : [];
        const merged = new Set(fromApi.filter(isIsoDate));
        (ensureDates || []).forEach((iso) => {
          if (isIsoDate(iso)) merged.add(iso);
        });
        const nextDates = [...merged].sort((a, b) => b.localeCompare(a));
        setDates(nextDates);
        setFields(fieldsFromExisting(nextDates, templateItems, existingByDate));
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || requestSeq !== requestSeqRef.current) return;
        const fallback = [...new Set((ensureDates || []).filter(isIsoDate))]
          .sort((a, b) => b.localeCompare(a));
        setDates(fallback);
        setFields(fieldsFromExisting(fallback, templateItems, existingByDate));
        setDatesError(
          err.response?.data?.message || err.message || "Failed to load dates with saved timesheet data."
        );
      } finally {
        if (requestSeq === requestSeqRef.current) setDatesLoading(false);
      }
    };

    loadDates();
    return () => controller.abort();
    // Snapshot props are fixed for the open session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectedDates = React.useMemo(
    () => dates.filter((iso) => !!selectedIds[iso]),
    [dates, selectedIds]
  );
  const selectedCount = selectedDates.length;
  const allSelected = dates.length > 0 && selectedCount === dates.length;
  const someSelected = selectedCount > 0 && !allSelected;

  React.useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const handleSelectionChange = (iso, checked) => {
    setSelectedIds((prev) => ({ ...prev, [iso]: checked }));
    setSaveMessage(null);
  };

  const handleToggleAll = (checked) => {
    if (!checked) {
      setSelectedIds({});
    } else {
      const next = {};
      dates.forEach((iso) => {
        next[iso] = true;
      });
      setSelectedIds(next);
    }
    setSaveMessage(null);
  };

  const handleInstalledChange = (workDate, itemKey, value) => {
    if (!isValidPartialNumberInput(value)) return;
    const key = fieldKey(workDate, itemKey);
    setFields((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || blankField()), newlyInstalled: value },
    }));
    setSaveMessage(null);
  };

  const handleInstalledBlur = (workDate, itemKey) => {
    const key = fieldKey(workDate, itemKey);
    setFields((prev) => {
      const current = prev[key] || blankField();
      if (current.newlyInstalled === "" || current.newlyInstalled == null) return prev;
      const parsed = parseFloat(current.newlyInstalled);
      const normalized = Number.isFinite(parsed) ? String(Math.max(0, parsed)) : "0";
      if (current.newlyInstalled === normalized) return prev;
      return { ...prev, [key]: { ...current, newlyInstalled: normalized } };
    });
  };

  const handleNotesChange = (workDate, itemKey, value) => {
    const key = fieldKey(workDate, itemKey);
    setFields((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || blankField()), dailyNotes: value },
    }));
    setSaveMessage(null);
  };

  const handleSave = async () => {
    if (saving) return;
    if (selectedCount === 0) {
      setSaveMessage({ type: "error", text: "Select at least one date." });
      return;
    }
    if ((templateItems || []).length === 0) {
      setSaveMessage({ type: "error", text: "No production quantities to update." });
      return;
    }

    const updates = [];
    for (const workDate of selectedDates) {
      const entries = [];
      for (const item of templateItems) {
        const field = fields[fieldKey(workDate, item.itemKey)] || blankField();
        const raw = String(field.newlyInstalled ?? "").trim();
        if (raw === "" || raw === ".") {
          setSaveMessage({
            type: "error",
            text: `Enter Newly Installed for ${formatIsoDateLabel(workDate)}.`,
          });
          return;
        }
        const newlyInstalled = parseNumber(raw);
        const dailyNotes = String(field.dailyNotes ?? "").trim();
        if (newlyInstalled !== 0 && !dailyNotes) {
          setSaveMessage({
            type: "error",
            text: `Daily notes are required for ${formatIsoDateLabel(workDate)} when Newly Installed is not 0.`,
          });
          return;
        }
        entries.push({
          itemKey: item.itemKey,
          itemName: item.name,
          newlyInstalled,
          dailyNotes,
        });
      }
      updates.push({ workDate, entries });
    }

    try {
      setSaving(true);
      setSaveMessage(null);
      await onSave?.({ updates });
      onClose?.();
    } catch (err) {
      setSaveMessage({
        type: "error",
        text: err?.message || "Failed to save production tracking.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const canSave = selectedCount > 0 && templateItems.length > 0 && !datesLoading && !saving;

  return createPortal(
    <div className="fixed inset-0 z-[10340] bg-black/50">
      <div className="absolute inset-2 lg:inset-6 flex min-h-0 justify-center">
        <div className="flex h-full max-h-full min-h-0 w-full max-w-[980px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
          <header className="relative z-10 flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-[18px] rounded-t-xl shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
            <h2 className="font-poppins text-[20px] font-bold text-[#2C2C2C]">
              Update Multiple
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100 disabled:opacity-50"
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-4 sm:p-6 [-webkit-overflow-scrolling:touch]">
            {templateItems.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No production quantities for this activity.
              </div>
            ) : datesLoading ? (
              <div className="py-8 text-center text-gray-500">
                Loading dates with saved timesheet data…
              </div>
            ) : (
              <>
                {datesError ? (
                  <p className="mb-4 text-sm text-red-600">{datesError}</p>
                ) : null}
                {dates.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    No dates with saved timesheet data for this activity.
                  </div>
                ) : (
                  <>
                    <section>
                      <h2 className="text-base font-semibold text-neutral-700 mb-4">
                        Select Dates
                      </h2>
                      <label className="mb-4 flex w-fit gap-2 items-center cursor-pointer">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => handleToggleAll(e.target.checked)}
                          aria-label="Select all dates"
                          className="rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] shrink-0 cursor-pointer"
                        />
                        <span className="font-poppins text-[14px] font-semibold text-[#2C2C2C]">
                          Select all
                        </span>
                      </label>
                      <div className="flex flex-wrap gap-4 content-center items-center max-sm:gap-3">
                        {dates.map((iso) => (
                          <label
                            key={iso}
                            className="flex gap-2 items-center bg-white rounded-sm cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={!!selectedIds[iso]}
                              onChange={(e) => handleSelectionChange(iso, e.target.checked)}
                              className="rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] shrink-0 cursor-pointer"
                            />
                            <span className="font-poppins text-[14px] font-normal text-[#2C2C2C]">
                              {formatIsoDateLabel(iso)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </section>

                    <section className="mt-6">
                      <h2 className="text-base font-semibold text-neutral-700 mb-4">
                        Production
                      </h2>
                      <div className="overflow-x-auto">
                        <div className="min-w-[720px] border border-gray-300 rounded-md overflow-hidden">
                          <div className="grid grid-cols-[120px_minmax(0,1.4fr)_160px_minmax(180px,1fr)] border-b border-gray-300 bg-gray-200">
                            {["Date", "Production Quantity", "Newly Installed", "Daily Notes"].map((label) => (
                              <div
                                key={label}
                                className="flex h-14 items-center justify-center px-2 text-center text-xs sm:text-sm font-semibold text-neutral-700"
                              >
                                {label}
                              </div>
                            ))}
                          </div>
                          {selectedDates.map((workDate) =>
                              templateItems.map((item, itemIndex) => {
                                const field = fields[fieldKey(workDate, item.itemKey)] || blankField();
                                const showDate = itemIndex === 0;
                                return (
                                  <div
                                    key={fieldKey(workDate, item.itemKey)}
                                    className="grid grid-cols-[120px_minmax(0,1.4fr)_160px_minmax(180px,1fr)] border-b border-gray-200 last:border-b-0"
                                  >
                                    <div className="flex min-h-[56px] items-center justify-center bg-gray-100 px-2 text-sm text-zinc-800">
                                      {showDate ? formatIsoDateLabel(workDate) : ""}
                                    </div>
                                    <div className="flex min-h-[56px] items-center justify-center bg-[#EAF4FB] px-3 text-center text-sm text-zinc-800">
                                      {item.name}
                                    </div>
                                    <div className="flex min-h-[56px] items-center justify-center bg-white px-3">
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        value={field.newlyInstalled}
                                        onChange={(e) => handleInstalledChange(workDate, item.itemKey, e.target.value)}
                                        onBlur={() => handleInstalledBlur(workDate, item.itemKey)}
                                        onFocus={(e) => e.target.select()}
                                        aria-label={`Newly Installed for ${formatIsoDateLabel(workDate)}`}
                                        className="h-[38px] w-full max-w-[120px] rounded border border-gray-300 bg-white text-center text-sm text-[#2C2C2C] outline-none"
                                      />
                                    </div>
                                    <div className="flex min-h-[56px] items-center justify-center bg-white px-3">
                                      <input
                                        type="text"
                                        value={field.dailyNotes}
                                        onChange={(e) => handleNotesChange(workDate, item.itemKey, e.target.value)}
                                        aria-label={`Daily Notes for ${formatIsoDateLabel(workDate)}`}
                                        placeholder="Daily Notes"
                                        className="h-[38px] w-full rounded border border-gray-300 bg-white px-2 text-sm text-[#2C2C2C] outline-none placeholder:text-gray-400"
                                      />
                                    </div>
                                  </div>
                                );
                              })
                            )}
                        </div>
                      </div>
                    </section>

                    <div className="mt-8 mb-2">
                      {saveMessage ? (
                        <p
                          className={`text-sm text-center mb-2 ${
                            saveMessage.type === "success" ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {saveMessage.text}
                        </p>
                      ) : null}
                      <div className="max-w-[296px] mx-auto">
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={!canSave}
                          className={`inline-flex justify-center items-center h-[47px] w-full rounded text-white text-lg ${
                            canSave
                              ? "bg-[#AD2226] hover:bg-[#8F1B1E] cursor-pointer"
                              : "bg-gray-200 cursor-not-allowed"
                          }`}
                        >
                          {saving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ProductionTrackingUpdateMultipleModal;
